import React, { useState, useEffect } from "react";
import {
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Select,
    Checkbox,
    MenuItem,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    InputAdornment,
    Button,
    Typography,
    Autocomplete,
    FormHelperText,
    Tooltip,
    Avatar,
    Box,
    useMediaQuery,
    Badge,
    IconButton,
    CircularProgress
} from "@mui/material";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import { getUrl } from "aws-amplify/storage";
import LoadingButton from "@mui/lab/LoadingButton";
import MuiPhoneNumber from "material-ui-phone-number";
import UploadPhotoModal from "../../components/UploadPhotoModal";
import { useForm, Controller } from "react-hook-form";
import moment from "moment-timezone";
import { useAtom } from "jotai";
import {
    selectedUserAtom,
    selectedUserIdAtom,
    userPageSnackbarAtom,
    openUserDialogAtom,
    updateGoogleIntegrationCalInfoAtom,
    selectedProviderAtom
} from "../../atoms/usersTable";
import { selectedBookingAtom } from "../../atoms/bookingsTable";
import {
    allActiveServicesAtom,
    allActiveSkillsAtom,
    allUsersAtom,
    mbxUserAtom,
    updateUserInAllUsersAtom,
    deleteUserInAllUsersAtom,
    updateProviderInVisibleBookingsAtom,
    isMbxUserProvider,
    companyMaxTravelTimeAtom,
    companyProviderRestrictionConfigAtom,
    companyMasterTz,
    companyAtom,
    isMbxUserManager,
    isMbxUserVerifiedStaff,
    isMbxUserAdmin as isMbxUserAdminAtom,
    conformationDialogOpenAtom,
    conformationDialogContentAtom,
    confirmationFunctionAtom,
    conformationDialogTitleAtom,
    conformationDialogSubmitButtonAtom,
    conformationDialogIsSubmittingAtom,
    warningIconInHeadingAtom
} from "../../atoms/atoms";
import {
    postApi,
    putApi,
    getJsonApi,
    delApiWithStatus,
    graphql,
    graphqlOperation
} from "../../modules/AmplifyServices";
import { isUniqueEmailAddress } from "../UserCommon";
import {
    DEFAULT_BUFFER_TIME_MINS,
    EMAIL_REGEX,
    GOOGLE_CAL_INTEGRATION_ID
} from "../../utils/Constants";
import { useTheme } from "@mui/material/styles";
import PasswordManagementModal from "../../components/PasswordManagmentModal";
import GoogleCalIntegration from "./GoogleCalIntegrations";
import { getRefData } from "../../graphql/queries";
import { jsonParse } from "../../utils";
import { useQuery } from "@tanstack/react-query";

const ProfileTab = ({ setInitialFirstName, setInitialLastName, onClose }) => {
    const theme = useTheme();
    const [mbxUser] = useAtom(mbxUserAtom);
    const [isVerifiedStaff] = useAtom(isMbxUserVerifiedStaff);
    const [company] = useAtom(companyAtom);
    const [isLoggedInUserProvider] = useAtom(isMbxUserProvider);
    const [isLoggedInUserManager] = useAtom(isMbxUserManager);
    const [selectedBooking] = useAtom(selectedBookingAtom);
    const [, updateProviderInVisibleBookings] = useAtom(
        updateProviderInVisibleBookingsAtom
    );
    const [, setGoogleCalInfo] = useAtom(updateGoogleIntegrationCalInfoAtom);
    const [selectedUser] = useAtom(selectedUserAtom);
    const [selectedProvider] = useAtom(selectedProviderAtom);
    const [isMbxUserAdmin] = useAtom(isMbxUserAdminAtom);
    const [, setSelectedUserId] = useAtom(selectedUserIdAtom);
    const [allActiveServices] = useAtom(allActiveServicesAtom);
    const [allActiveSkills] = useAtom(allActiveSkillsAtom);
    const [companyMaxTravelTime] = useAtom(companyMaxTravelTimeAtom);
    const [providerRestrictionConfig] = useAtom(
        companyProviderRestrictionConfigAtom
    );
    const [masterTz] = useAtom(companyMasterTz);
    const [allUsers, setAllUsers] = useAtom(allUsersAtom);
    const [, setUserPageSnackbar] = useAtom(userPageSnackbarAtom);
    const [, updateUserInAllUsers] = useAtom(updateUserInAllUsersAtom);
    const [, deleteUserInAllUsers] = useAtom(deleteUserInAllUsersAtom);
    const [, setOpenUserDialog] = useAtom(openUserDialogAtom);
    const [, setConfirmationDialogContent] = useAtom(
        conformationDialogContentAtom
    );
    const [, setConfirmationFunction] = useAtom(confirmationFunctionAtom);
    const [, setConfirmationDialogOpen] = useAtom(conformationDialogOpenAtom);
    const [, setConformationDialogTitle] = useAtom(conformationDialogTitleAtom);
    const [, setConformationDialogSubmitButton] = useAtom(
        conformationDialogSubmitButtonAtom
    );
    const [, setConformationDialogIsSubmitting] = useAtom(
        conformationDialogIsSubmittingAtom
    );
    const [, setWarningIconInHeading] = useAtom(warningIconInHeadingAtom);
    const [openUploadPhotoDialog, setOpenUploadPhotoDialog] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [s3Link, setS3Link] = useState("");
    const timezoneMenuItems = moment.tz.names();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true
    });
    const isManagerView =
        isLoggedInUserManager &&
        selectedUser &&
        mbxUser.id !== selectedUser?.id &&
        selectedUser?.role !== "PROVIDER";

    const disableAllFields = isSubmitting || isManagerView;
    //Password management states
    const [passwordManagementType, setPasswordManagementType] = useState();
    const [showManagePasswordDialog, setShowManagePasswordDialog] =
        useState(false);
    const [showResetPasswordSpinner, setShowResetPasswordSpinner] =
        useState(false);
    const {
        active = true,
        firstname: firstName = "",
        lastname: lastName = "",
        emailaddress: email = "",
        mobilephone: phone = "",
        role: userRole = "PROVIDER",
        provider = {},
        contactconsent: sendDailyAgendaMail = true
    } = selectedUser || {};
    let {
        timezone,
        offersMobileServices = true,
        offersVirtualServices = false,
        vmlink = "",
        bio = "",
        maxTravelTime: providerMaxTravelTime = companyMaxTravelTime || 30,
        servicetypes: { items: providerServices = [] } = {},
        skills: { items: providerSkills = [] } = {},
        pictures3key = null
    } = provider || {};
    if (!timezone) {
        timezone = masterTz || "";
    }
    if (offersMobileServices === null) {
        offersMobileServices = true;
    }
    const form = useForm({
        defaultValues: {
            active,
            firstName,
            lastName,
            email,
            phone,
            role: userRole,
            timezone,
            availableForInHome: offersMobileServices,
            bufferTime: DEFAULT_BUFFER_TIME_MINS,
            maxTravelTime:
                ((isLoggedInUserProvider &&
                    providerRestrictionConfig.canProviderSetMaxTravelTime) ||
                    isVerifiedStaff) &&
                providerMaxTravelTime
                    ? providerMaxTravelTime
                    : companyMaxTravelTime,
            availableForVirtual: offersVirtualServices,
            personalMeetingLink: vmlink?.replace("https://", ""),
            services: selectedUser
                ? providerServices
                      .filter((item) =>
                          allActiveServices.some(
                              (service) => service.id === item.servicetype.id
                          )
                      )
                      .map((item) => item.servicetype.id)
                : allActiveServices.map((service) => service.id),
            skills: selectedUser
                ? providerSkills
                      .filter((item) =>
                          allActiveSkills.some(
                              (skill) => skill.id === item.skill.id
                          )
                      )
                      .map((item) => item.skill.id)
                : allActiveSkills.map((skill) => skill.id),
            bio,
            sendDailyAgendaMail
        },
        mode: "onSubmit",
        reValidateMode: "onSubmit"
    });
    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        clearErrors,
        setError,
        watch,
        control,
        formState
    } = form;
    const { errors, dirtyFields, isDirty } = formState;
    const availableForVirtual = watch("availableForVirtual");
    const availableForInHome = watch("availableForInHome");
    const role = watch("role");
    register("phone", { required: "Please enter your phone number" });

    const { isFetching: isFetchingGoogleCalIntegration } = useQuery({
        queryKey: ["initial-google-cal-integration"],
        queryFn: async () => {
            try {
                const { data, errors } = await graphql(
                    graphqlOperation(getRefData, {
                        refType: `integration|${selectedProvider.id}`,
                        refName: GOOGLE_CAL_INTEGRATION_ID
                    })
                );
                if (errors) throw errors;
                const googleIntegrationCalInfo = jsonParse(
                    data?.getRefData?.overrideValue,
                    null
                );

                setGoogleCalInfo(googleIntegrationCalInfo);
                return googleIntegrationCalInfo;
            } catch (e) {
                console.log(e);
            }
        }
    });

    useEffect(() => {
        const getS3Url = async () => {
            if (pictures3key) {
                const data = await getUrl({
                    key: pictures3key
                });
                setS3Link(data?.url);
            }
        };
        getS3Url();
    }, [pictures3key]);

    const handleOpenUploadPhotoDialog = () => {
        setOpenUploadPhotoDialog(true);
    };

    const handleCloseUploadPhotoDialog = () => {
        setOpenUploadPhotoDialog(false);
    };

    const handlePhoneChange = (value) => {
        setValue("phone", value, { shouldDirty: true });
        clearErrors("phone");
    };

    const handleValidateUniqueEmail = async (email) => {
        if (!dirtyFields.email) return true; //no need to validate email if it hasnt been changed
        try {
            const isUniqueEmail = await isUniqueEmailAddress(email);
            if (!isUniqueEmail) {
                setError("email", {
                    type: "focus",
                    message: "Email is already in use by another user"
                });
            }
            return isUniqueEmail;
        } catch (error) {
            console.log("Error while validating email", error);
        }
    };

    const handleCloseEmailWarningModal = async (validateEmail) => {
        if (validateEmail) {
            const isUniqueEmail = await handleValidateUniqueEmail(
                getValues("email")
            );
            if (isUniqueEmail) onSubmit(getValues(), true);
        }
        setConfirmationDialogOpen(false);
        setWarningIconInHeading(false);
    };

    async function handleResetPasswordClick() {
        setShowManagePasswordDialog(true);
        setShowResetPasswordSpinner(true);
        try {
            const result = await getJsonApi("changeuserrole", "/getuser", {
                queryParams: {
                    username: selectedUser.id
                }
            });
            if (result && result.success) {
                setShowResetPasswordSpinner(false);
                let userStatus = result.data.UserStatus;
                if (
                    userStatus === "CONFIRMED" ||
                    userStatus === "RESET_REQUIRED"
                ) {
                    //allow reset for these two statuses
                    setPasswordManagementType("RESET");
                } else if (userStatus === "FORCE_CHANGE_PASSWORD") {
                    setPasswordManagementType("SET-TEMP-PWD");
                }
            } else {
                setShowResetPasswordSpinner(false);
                setShowManagePasswordDialog(false);
            }
        } catch (e) {
            setShowResetPasswordSpinner(false);
            setShowManagePasswordDialog(false);
            console.log("Error: Unable to get cognito password data", e);
        }
    }

    const handleDeleteUser = async () => {
        setConformationDialogIsSubmitting(true);
        try {
            const { statusCode = null, message = "" } = await delApiWithStatus(
                "userManagement",
                `/user/profile/${selectedUser.id}`,
                {
                    queryParams: {
                        companyId: company.id,
                        providerId: selectedUser.id
                    }
                }
            );

            if (statusCode >= 200 && statusCode < 300) {
                setUserPageSnackbar({
                    open: true,
                    message: "User has been successfully deleted",
                    severity: "success"
                });
                deleteUserInAllUsers(selectedUser.id);
                setConformationDialogIsSubmitting(false);
                setConfirmationDialogOpen(false);
                onClose();
            } else {
                setUserPageSnackbar({
                    open: true,
                    message: `Unexpected error while deleteing user ${statusCode}`,
                    severity: "error"
                });
                setConformationDialogIsSubmitting(false);
                setConfirmationDialogOpen(false);
                throw new Error(
                    `Error deleting user profile. Error: ${message}`
                );
            }
        } catch (error) {
            console.log("Error deleting user:", error);
            setConformationDialogIsSubmitting(false);
            setConfirmationDialogOpen(false);
        }
    };

    const onSubmit = async (data, emailChangeAcknowledged) => {
        //Only open the email warning modal when the user is editing an existing user, the email field has been changed, and the email change warning has not been acknowledged
        if (
            selectedUser &&
            dirtyFields.email &&
            emailChangeAcknowledged !== true
        ) {
            setConfirmationFunction(handleCloseEmailWarningModal);
            setConfirmationDialogContent(
                <Typography>
                    Changing this user's email address will update their unique
                    identifier in the system and will affect all scheduled email
                    notifications to this person – do you wish to continue?
                </Typography>
            );
            setConformationDialogTitle("Change email");
            setWarningIconInHeading(true);
            setConformationDialogSubmitButton("Continue");
            setConfirmationDialogOpen(true);
            return;
        }
        setIsSubmitting(true);
        try {
            //append additional data for API use
            data = {
                ...data,
                companyId: company.id,
                useAnonymousPermalink: company.useAnonymousPermalink
            };
            if (!selectedUser) {
                //create route
                const {
                    status = null,
                    newUserRecord = null,
                    message = ""
                } = await postApi("userManagement", "/user/profile", {
                    body: data
                });

                if (newUserRecord && status >= 200 && status < 300) {
                    onSuccess(
                        newUserRecord,
                        "User has been successfully created"
                    );
                } else {
                    onFailure(
                        status,
                        "Unexpected error: The user account was not created"
                    );
                    throw new Error(
                        `Failed to create new user profile. Error: ${message}`
                    );
                }
            } else {
                //edit route
                const {
                    status = null,
                    updatedUser = null,
                    message = ""
                } = await putApi(
                    "userManagement",
                    `/user/profile/${selectedUser.id}`,
                    {
                        body: data
                    }
                );

                if (updatedUser && status >= 200 && status < 300) {
                    onSuccess(
                        updatedUser,
                        "User has been successfully updated"
                    );
                } else {
                    onFailure(
                        status,
                        "Unexpected error: The user account was not updated"
                    );
                    throw new Error(
                        `Failed to update user profile. Error: ${message}`
                    );
                }
            }
        } catch (error) {
            console.log("Error submitting form data:", error);
            setIsSubmitting(false);
        }
    };

    const onSuccess = async (userData, successMessage) => {
        if (!selectedUser) {
            //create route
            setAllUsers([
                {
                    LoginInfo: null,
                    active: userData.active,
                    company: company,
                    contactconsent: userData.contactconsent,
                    createdAt: userData.createdAt,
                    deleted: userData.deleted,
                    emailaddress: userData.emailaddress,
                    firstname: userData.firstname,
                    homephone: userData.lastname,
                    id: userData.id,
                    lastname: userData.lastname,
                    mobilephone: userData.mobilephone,
                    phonepref: userData.phonepref,
                    provider: userData.provider,
                    providerId: userData.providerId,
                    registered: userData.registered,
                    role: userData.role,
                    schedules: [],
                    workphone: null
                },
                ...allUsers
            ]);
            setSelectedUserId(userData.id);
        } else {
            // Edit route if selectedBooking from the booking list is present
            if (selectedUser && selectedBooking) {
                const updatedBookingData = {
                    providerId: selectedUser.providerId,
                    provider: {
                        ...selectedBooking.provider,
                        emailaddress: userData.emailaddress,
                        firstname: userData.firstname,
                        lastname: userData.lastname
                    }
                };
                updateUserInAllUsers(userData);
                updateProviderInVisibleBookings(updatedBookingData);
            } else {
                // Otherwise, just update user in the list
                updateUserInAllUsers(userData);
            }
            setOpenUserDialog(false);
        }
        setUserPageSnackbar({
            open: true,
            message: successMessage,
            severity: "success"
        });
        setIsSubmitting(false);
    };

    const onFailure = (status, errorMessage) => {
        setUserPageSnackbar({
            open: true,
            message: `${errorMessage} ${status ? `(${status})` : ""}`,
            severity: "error"
        });
    };

    const isLoading = isFetchingGoogleCalIntegration;

    return (
        <>
            <UploadPhotoModal
                open={openUploadPhotoDialog}
                onClose={handleCloseUploadPhotoDialog}
                setS3Link={setS3Link}
            />
            {selectedUser && (
                <PasswordManagementModal
                    showManagePasswordDialog={showManagePasswordDialog}
                    passwordManagementType={passwordManagementType}
                    setPasswordManagementType={setPasswordManagementType}
                    setShowManagePasswordDialog={setShowManagePasswordDialog}
                    showResetPasswordSpinner={showResetPasswordSpinner}
                    userData={{
                        userId: selectedUser?.id,
                        firstName: selectedUser?.firstname,
                        lastName: selectedUser?.lastname,
                        emailAddress: selectedUser?.emailaddress,
                        userRole: selectedUser?.role
                    }}
                    loggedInUser={mbxUser}
                    emailAddress={selectedUser?.emailaddress}
                />
            )}
            <form
                noValidate
                onSubmit={handleSubmit(onSubmit)}
                style={{
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%"
                }}
            >
                <DialogContent
                    sx={{ minHeight: "200px", alignContent: "center" }}
                >
                    {isLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid
                            container
                            spacing={2.25}
                            justifyContent={!isDesktop && "center"}
                            alignItems="flex-start"
                        >
                            {!isDesktop && selectedUser && (
                                <Grid item xs={12} sx={{ textAlign: "center" }}>
                                    <Badge
                                        overlap="circular"
                                        anchorOrigin={{
                                            vertical: "bottom",
                                            horizontal: "right"
                                        }}
                                        badgeContent={
                                            <IconButton
                                                aria-label="add-photo"
                                                onClick={
                                                    handleOpenUploadPhotoDialog
                                                }
                                                sx={{
                                                    backgroundColor: `${theme.palette.primary.main} !important`
                                                }}
                                            >
                                                <CameraAltOutlinedIcon
                                                    sx={{ color: "white" }}
                                                />
                                            </IconButton>
                                        }
                                    >
                                        <Avatar
                                            variant="rounded"
                                            src={s3Link}
                                            sx={{
                                                width: 84,
                                                height: 84,
                                                borderRadius: "50%"
                                            }}
                                            alt="User"
                                        >
                                            {`${selectedUser.firstname
                                                .charAt(0)
                                                .toUpperCase()}${selectedUser.lastname
                                                .charAt(0)
                                                .toUpperCase()}`}
                                        </Avatar>
                                    </Badge>
                                </Grid>
                            )}
                            {!isLoggedInUserProvider && (
                                <Grid item xs={12} sm={12}>
                                    <Controller
                                        name="active"
                                        control={control}
                                        render={({
                                            field: {
                                                onChange: onChangeFormHook,
                                                value
                                            }
                                        }) => (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        id="active"
                                                        name="active"
                                                        disabled={
                                                            disableAllFields
                                                        }
                                                        checked={value}
                                                        value={value}
                                                        onChange={
                                                            onChangeFormHook
                                                        }
                                                    />
                                                }
                                                label="Active"
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                            {selectedUser && (
                                <Grid item xs={12} sm={12}>
                                    <GoogleCalIntegration />
                                </Grid>
                            )}
                            <>
                                <Grid item xs={6} sm={6}>
                                    <TextField
                                        id="firstname"
                                        name="firstName"
                                        type="text"
                                        size="small"
                                        label="First name"
                                        disabled={disableAllFields}
                                        fullWidth
                                        value={getValues("firstName")}
                                        inputProps={{
                                            onBlur: (event) => {
                                                const value =
                                                    event.target.value;
                                                if (value) {
                                                    const formattedName =
                                                        value[0].toUpperCase() +
                                                        value.slice(1);
                                                    setValue(
                                                        "firstName",
                                                        formattedName
                                                    );
                                                    setInitialFirstName(
                                                        formattedName
                                                    );
                                                }
                                            },
                                            onChange: (event) => {
                                                setValue(
                                                    "firstName",
                                                    event.target.value
                                                );
                                            }
                                        }}
                                        sx={{
                                            "& fieldset": {
                                                "& legend": {
                                                    width: "58px"
                                                }
                                            }
                                        }}
                                        {...register("firstName", {
                                            required:
                                                "Please enter your first name",
                                            onChange: () =>
                                                clearErrors("firstName")
                                        })}
                                        error={!!errors.firstName}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={6}>
                                    <TextField
                                        id="lastname"
                                        name="lastName"
                                        type="text"
                                        size="small"
                                        label="Last name"
                                        disabled={disableAllFields}
                                        value={getValues("lastName")}
                                        fullWidth
                                        inputProps={{
                                            onBlur: (event) => {
                                                const value =
                                                    event.target.value;
                                                if (value) {
                                                    const formattedName =
                                                        value[0].toUpperCase() +
                                                        value.slice(1);
                                                    setValue(
                                                        "lastName",
                                                        formattedName
                                                    );
                                                    setInitialLastName(
                                                        formattedName
                                                    );
                                                }
                                            },
                                            onChange: (event) => {
                                                setValue(
                                                    "lastName",
                                                    event.target.value
                                                );
                                            }
                                        }}
                                        sx={{
                                            "& fieldset": {
                                                "& legend": {
                                                    width: "58px"
                                                }
                                            }
                                        }}
                                        {...register("lastName", {
                                            required:
                                                "Please enter your last name",
                                            onChange: () =>
                                                clearErrors("lastName")
                                        })}
                                        error={!!errors.lastName}
                                    />
                                </Grid>
                            </>

                            {(errors?.firstName || errors?.lastName) && (
                                <>
                                    <Grid
                                        item
                                        variant="errorTextContainer"
                                        xs={12}
                                        sm={6}
                                    >
                                        <Typography variant="errorText">
                                            {errors.firstName?.message}
                                        </Typography>
                                    </Grid>
                                    <Grid
                                        variant="errorTextContainer"
                                        item
                                        xs={12}
                                        sm={6}
                                    >
                                        <Typography variant="errorText">
                                            {errors.lastName?.message}
                                        </Typography>
                                    </Grid>
                                </>
                            )}

                            <>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        id="email"
                                        name="email"
                                        type="email"
                                        size="small"
                                        label="Email"
                                        disabled={disableAllFields}
                                        fullWidth
                                        sx={{
                                            "& fieldset": {
                                                "& legend": {
                                                    width: "36px"
                                                }
                                            }
                                        }}
                                        {...register("email", {
                                            required: "Please enter your email",
                                            pattern: {
                                                value: EMAIL_REGEX,
                                                message: "Invalid email format"
                                            },
                                            validate: {
                                                validateUniqueEmail: async (
                                                    fieldValue
                                                ) =>
                                                    selectedUser &&
                                                    dirtyFields.email
                                                        ? true //true is only passed here to bypass form validation, the EmailChangeWarningModal will be triggered for email validation in this scenerio
                                                        : (await handleValidateUniqueEmail(
                                                              fieldValue
                                                          )) ||
                                                          "Email is already in use by another user"
                                            },
                                            onChange: () => clearErrors("email")
                                        })}
                                        error={!!errors.email}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MuiPhoneNumber
                                        id="phone"
                                        variant="outlined"
                                        label="Phone"
                                        disabled={disableAllFields}
                                        sx={{
                                            display: "flex",
                                            "& fieldset": {
                                                "& legend": {
                                                    width: "39px"
                                                }
                                            }
                                        }}
                                        size="small"
                                        fullWidth
                                        defaultCountry={
                                            company?.countrycode3166alpha2
                                                ? company.countrycode3166alpha2.toLowerCase()
                                                : "ca"
                                        }
                                        value={getValues("phone")}
                                        onChange={handlePhoneChange}
                                        error={
                                            !!errors.phone &&
                                            !getValues("phone")
                                        }
                                    />
                                </Grid>
                            </>

                            {(errors?.email ||
                                (!getValues("phone") && errors?.phone)) && (
                                <>
                                    <Grid
                                        item
                                        variant="errorTextContainer"
                                        xs={12}
                                        sm={6}
                                    >
                                        <Typography variant="errorText">
                                            {errors.email?.message}
                                        </Typography>
                                    </Grid>
                                    <Grid
                                        variant="errorTextContainer"
                                        item
                                        xs={12}
                                        sm={6}
                                    >
                                        <Typography variant="errorText">
                                            {errors.phone?.message}
                                        </Typography>
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={12} sm={6}>
                                <FormControl
                                    fullWidth
                                    disabled={
                                        disableAllFields ||
                                        isLoggedInUserProvider ||
                                        isLoggedInUserManager
                                    }
                                >
                                    <InputLabel id="role-label" size="small">
                                        Role
                                    </InputLabel>
                                    <Controller
                                        name="role"
                                        control={control}
                                        rules={{
                                            required: "Please select a role"
                                        }}
                                        render={({
                                            field: {
                                                onChange: onChangeFormHook,
                                                value
                                            }
                                        }) => (
                                            <>
                                                <Select
                                                    id="role"
                                                    name="role"
                                                    labelId="role-label"
                                                    label="Role"
                                                    sx={{
                                                        "& fieldset": {
                                                            "& legend": {
                                                                width: "30px"
                                                            }
                                                        }
                                                    }}
                                                    value={role}
                                                    onChange={(e) => {
                                                        onChangeFormHook(
                                                            e.target.value
                                                        );
                                                    }}
                                                >
                                                    <MenuItem
                                                        value={"PROVIDER"}
                                                    >
                                                        Provider
                                                    </MenuItem>

                                                    <MenuItem
                                                        value={
                                                            "MANAGER_PROVIDER"
                                                        }
                                                    >
                                                        Manager
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={
                                                            "COMPANY_ADMIN_PROVIDER"
                                                        }
                                                    >
                                                        Admin
                                                    </MenuItem>
                                                </Select>
                                                {isMbxUserAdmin && (
                                                    <FormHelperText>
                                                        <Typography
                                                            variant="helperText"
                                                            sx={{
                                                                letterSpacing:
                                                                    "-0.01em"
                                                            }}
                                                        >
                                                            Admins and managers
                                                            can already act as
                                                            bookable providers
                                                        </Typography>
                                                    </FormHelperText>
                                                )}
                                            </>
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <Controller
                                        name="timezone"
                                        control={control}
                                        rules={{
                                            required: "Please select a timezone"
                                        }}
                                        render={({
                                            field: {
                                                onChange: onChangeFormHook,
                                                value
                                            }
                                        }) => (
                                            <Autocomplete
                                                id="timezone"
                                                name="timezone"
                                                size="small"
                                                options={timezoneMenuItems}
                                                disabled={disableAllFields}
                                                disableClearable={true}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Timezone"
                                                    />
                                                )}
                                                value={value}
                                                onChange={(e) => {
                                                    onChangeFormHook(
                                                        e.target.textContent
                                                    );
                                                }}
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="availableForInHome"
                                    control={control}
                                    render={({
                                        field: {
                                            onChange: onChangeFormHook,
                                            value
                                        }
                                    }) => (
                                        <FormControlLabel
                                            sx={{ height: 32 }}
                                            control={
                                                <Switch
                                                    id="availableForInHome"
                                                    name="availableForInHome"
                                                    checked={value}
                                                    disabled={disableAllFields}
                                                    value={value}
                                                    onChange={onChangeFormHook}
                                                />
                                            }
                                            label="Available for in-home appointments"
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Tooltip
                                    title={
                                        disableAllFields ||
                                        !availableForInHome ||
                                        (isLoggedInUserProvider &&
                                            !providerRestrictionConfig.canProviderSetMaxTravelTime)
                                            ? "Max travel time between bookings is controlled at the company level – please contact your administrator to change this setting"
                                            : ""
                                    }
                                    placement="top"
                                    slotProps={{
                                        tooltip: {
                                            sx: {
                                                pointerEvents: "auto"
                                            }
                                        }
                                    }}
                                >
                                    <FormControl
                                        sx={{
                                            width: "calc(50% - 9px)",
                                            marginRight: "18px"
                                        }}
                                        disabled={
                                            disableAllFields ||
                                            !availableForInHome ||
                                            (isLoggedInUserProvider &&
                                                !providerRestrictionConfig.canProviderSetMaxTravelTime)
                                        }
                                    >
                                        <InputLabel
                                            id="traveltime-label"
                                            size="small"
                                        >
                                            Max travel time
                                        </InputLabel>
                                        <Controller
                                            name="maxTravelTime"
                                            control={control}
                                            rules={{
                                                required: availableForInHome
                                                    ? "Please select a max travel time"
                                                    : false
                                            }}
                                            render={({
                                                field: {
                                                    onChange: onChangeFormHook,
                                                    value
                                                }
                                            }) => (
                                                <Select
                                                    labelId="traveltime-label"
                                                    id="traveltime"
                                                    name="maxTravelTime"
                                                    label="Max travel time"
                                                    disabled={
                                                        disableAllFields ||
                                                        !availableForInHome ||
                                                        (isLoggedInUserProvider &&
                                                            !providerRestrictionConfig.canProviderSetMaxTravelTime)
                                                    }
                                                    value={value}
                                                    onChange={onChangeFormHook}
                                                >
                                                    <MenuItem value={5}>
                                                        5 mins
                                                    </MenuItem>
                                                    <MenuItem value={10}>
                                                        10 mins
                                                    </MenuItem>
                                                    <MenuItem value={15}>
                                                        15 mins
                                                    </MenuItem>
                                                    <MenuItem value={20}>
                                                        20 mins
                                                    </MenuItem>
                                                    <MenuItem value={25}>
                                                        25 mins
                                                    </MenuItem>
                                                    <MenuItem value={30}>
                                                        30 mins
                                                    </MenuItem>
                                                    <MenuItem value={45}>
                                                        45 mins
                                                    </MenuItem>
                                                    <MenuItem value={60}>
                                                        60 mins
                                                    </MenuItem>
                                                    <MenuItem value={90}>
                                                        90 mins
                                                    </MenuItem>
                                                    <MenuItem value={120}>
                                                        120 mins
                                                    </MenuItem>
                                                </Select>
                                            )}
                                        />
                                    </FormControl>
                                </Tooltip>
                                <FormControl
                                    disabled={true}
                                    sx={{ width: "calc(50% - 9px)" }}
                                >
                                    <InputLabel
                                        id="buffertime-label"
                                        size="small"
                                    >
                                        Buffer time
                                    </InputLabel>
                                    <Controller
                                        name="bufferTime"
                                        control={control}
                                        rules={{
                                            required: availableForInHome
                                                ? "Please select buffer time"
                                                : false
                                        }}
                                        render={({
                                            field: {
                                                onChange: onChangeFormHook,
                                                value
                                            }
                                        }) => (
                                            <Select
                                                labelId="buffertime-label"
                                                id="buffertime"
                                                name="bufferTime"
                                                label="Buffer time"
                                                disabled={true}
                                                value={value}
                                                onChange={onChangeFormHook}
                                            >
                                                <MenuItem value={10}>
                                                    10 mins
                                                </MenuItem>
                                                <MenuItem value={15}>
                                                    15 mins
                                                </MenuItem>
                                                <MenuItem value={20}>
                                                    20 mins
                                                </MenuItem>
                                                <MenuItem value={25}>
                                                    25 mins
                                                </MenuItem>
                                                <MenuItem value={30}>
                                                    30 mins
                                                </MenuItem>
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                                {/* </Grid>
                                </Grid>*/}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="availableForVirtual"
                                    control={control}
                                    render={({
                                        field: {
                                            onChange: onChangeFormHook,
                                            value
                                        }
                                    }) => (
                                        <FormControlLabel
                                            sx={{ height: 32 }}
                                            control={
                                                <Switch
                                                    id="availableForVirtual"
                                                    name="availableForVirtual"
                                                    disabled={disableAllFields}
                                                    checked={value}
                                                    value={value}
                                                    onChange={(event) => {
                                                        onChangeFormHook(event);
                                                        if (
                                                            errors.personalMeetingLink
                                                        ) {
                                                            clearErrors(
                                                                "personalMeetingLink"
                                                            );
                                                        }
                                                    }}
                                                />
                                            }
                                            label="Available for virtual appointments"
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    id="url"
                                    name="personalMeetingLink"
                                    size="small"
                                    label="Virtual meeting link"
                                    disabled={
                                        disableAllFields || !availableForVirtual
                                    }
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment
                                                position="start"
                                                sx={{ width: "40px" }}
                                            >
                                                <Typography
                                                    sx={{
                                                        color: !availableForVirtual
                                                            ? `${theme.palette.helperText.main}`
                                                            : "inherit"
                                                    }}
                                                >
                                                    https://
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                        onBlur: (e) => {
                                            e.target.value =
                                                e.target.value.startsWith(
                                                    "https://"
                                                )
                                                    ? e.target.value.replace(
                                                          "https://",
                                                          ""
                                                      )
                                                    : e.target.value;
                                        },
                                        onKeyDown: (e) => {
                                            if (e.key === "Enter") {
                                                e.target.value =
                                                    e.target.value.startsWith(
                                                        "https://"
                                                    )
                                                        ? e.target.value.replace(
                                                              "https://",
                                                              ""
                                                          )
                                                        : e.target.value;
                                            }
                                        }
                                    }}
                                    sx={{
                                        "& fieldset": {
                                            "& legend": {
                                                width: "109px"
                                            }
                                        }
                                    }}
                                    {...register("personalMeetingLink", {
                                        required: availableForVirtual
                                            ? "Please enter your virtual meeting link"
                                            : false,
                                        onBlur: (e) =>
                                            e.target.value.startsWith(
                                                "https://"
                                            ) &&
                                            setValue(
                                                "personalMeetingLink",
                                                e.target.value.slice(8)
                                            )
                                    })}
                                    error={!!errors.personalMeetingLink}
                                    helperText={
                                        errors.personalMeetingLink ? (
                                            <Typography variant="errorText">
                                                {
                                                    errors.personalMeetingLink
                                                        .message
                                                }
                                            </Typography>
                                        ) : null
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <FormControl
                                    fullWidth
                                    error={!!errors.services}
                                    disabled={disableAllFields}
                                >
                                    <InputLabel
                                        id="services-label"
                                        size="small"
                                    >
                                        Services
                                    </InputLabel>
                                    <Controller
                                        name="services"
                                        control={control}
                                        render={({
                                            field: {
                                                onChange: onChangeFormHook,
                                                value
                                            }
                                        }) => {
                                            const validServices = value.filter(
                                                (serviceId) =>
                                                    allActiveServices.some(
                                                        (service) =>
                                                            service.id ===
                                                            serviceId
                                                    )
                                            );

                                            if (
                                                validServices.length !==
                                                value.length
                                            ) {
                                                onChangeFormHook(validServices);
                                            }

                                            return (
                                                <Select
                                                    labelId="services-label"
                                                    id="services"
                                                    name="services"
                                                    label="Services"
                                                    multiple
                                                    sx={{
                                                        "& fieldset": {
                                                            "& legend": {
                                                                width: "49px"
                                                            }
                                                        }
                                                    }}
                                                    value={validServices}
                                                    onChange={(e) =>
                                                        onChangeFormHook(
                                                            e.target.value
                                                        )
                                                    }
                                                    renderValue={() =>
                                                        validServices.length ===
                                                        allActiveServices.length
                                                            ? "All services"
                                                            : allActiveServices
                                                                  .filter(
                                                                      (
                                                                          service
                                                                      ) =>
                                                                          validServices.includes(
                                                                              service.id
                                                                          )
                                                                  )
                                                                  .map(
                                                                      (
                                                                          service
                                                                      ) =>
                                                                          service.name
                                                                  )
                                                                  .join(", ")
                                                    }
                                                >
                                                    {allActiveServices.map(
                                                        (service) => (
                                                            <MenuItem
                                                                value={
                                                                    service.id
                                                                }
                                                                key={service.id}
                                                            >
                                                                <Checkbox
                                                                    checked={validServices.includes(
                                                                        service.id
                                                                    )}
                                                                />
                                                                {service.name}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </Select>
                                            );
                                        }}
                                    />
                                    {!!errors.services && (
                                        <FormHelperText error>
                                            {errors?.services.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <FormControl
                                    fullWidth
                                    error={!!errors.skills}
                                    disabled={disableAllFields}
                                >
                                    <InputLabel id="skills-label" size="small">
                                        Skills
                                    </InputLabel>
                                    <Controller
                                        name="skills"
                                        control={control}
                                        render={({
                                            field: {
                                                onChange: onChangeFormHook,
                                                value
                                            }
                                        }) => (
                                            <Select
                                                labelId="skills-label"
                                                id="skills"
                                                name="skills"
                                                label="Skills"
                                                multiple
                                                sx={{
                                                    "& fieldset": {
                                                        "& legend": {
                                                            width: "33px"
                                                        }
                                                    }
                                                }}
                                                value={value}
                                                onChange={(e) => {
                                                    onChangeFormHook(
                                                        e.target.value
                                                    );
                                                }}
                                                renderValue={() =>
                                                    value.length ===
                                                    allActiveSkills.length
                                                        ? "All skills"
                                                        : allActiveSkills
                                                              .filter((skill) =>
                                                                  value.includes(
                                                                      skill.id
                                                                  )
                                                              )
                                                              .map(
                                                                  (skill) =>
                                                                      skill.name
                                                              )
                                                              .join(", ")
                                                }
                                            >
                                                {allActiveSkills.map(
                                                    (skill) => (
                                                        <MenuItem
                                                            value={skill.id}
                                                        >
                                                            <Checkbox
                                                                checked={value.includes(
                                                                    skill.id
                                                                )}
                                                            />
                                                            {skill.name}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Select>
                                        )}
                                    />
                                    {!!errors.skills && (
                                        <FormHelperText error>
                                            {errors?.skills.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <TextField
                                    id="bio"
                                    name="bio"
                                    label="Bio"
                                    size="small"
                                    disabled={disableAllFields}
                                    multiline
                                    fullWidth
                                    variant="outlined"
                                    rows={3}
                                    sx={{
                                        "& fieldset": {
                                            "& legend": {
                                                width: "23px"
                                            }
                                        }
                                    }}
                                    {...register("bio")}
                                    error={!!errors.bio}
                                    helperText={
                                        errors.bio ? (
                                            <Typography variant="errorText">
                                                {errors.bio.message}
                                            </Typography>
                                        ) : null
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="sendDailyAgendaMail"
                                    control={control}
                                    render={({
                                        field: {
                                            onChange: onChangeFormHook,
                                            value
                                        }
                                    }) => (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    id="sendDailyAgendaMail"
                                                    name="sendDailyAgendaMail"
                                                    disabled={disableAllFields}
                                                    checked={value}
                                                    value={value}
                                                    onChange={onChangeFormHook}
                                                />
                                            }
                                            label="Send daily agenda email"
                                        />
                                    )}
                                />
                            </Grid>
                            {selectedUser && !isLoggedInUserProvider && (
                                <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    sx={{
                                        paddingTop: "36px",
                                        paddingLeft: "18px"
                                    }}
                                >
                                    <Button
                                        variant="text"
                                        sx={{
                                            "&:hover": {
                                                backgroundColor:
                                                    "transparent !important"
                                            }
                                        }}
                                        disabled={disableAllFields}
                                        onClick={handleResetPasswordClick}
                                        disableRipple
                                    >
                                        <Typography
                                            variant="button"
                                            sx={{
                                                textTransform: "none",
                                                lineHeight: "16px"
                                            }}
                                        >
                                            {`Reset password`}
                                        </Typography>
                                    </Button>
                                </Grid>
                            )}
                            {selectedUser && isVerifiedStaff && (
                                <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    sx={{
                                        paddingTop: "36px",
                                        paddingLeft: "18px"
                                    }}
                                >
                                    <Button
                                        variant="text"
                                        color="error"
                                        onClick={() => {
                                            setConfirmationFunction(() =>
                                                handleDeleteUser()
                                            );
                                            setConfirmationDialogContent(
                                                <Box>
                                                    <Typography
                                                        variant="body2"
                                                        marginBottom="16px"
                                                    >
                                                        Are you sure you wish to
                                                        delete {firstName}{" "}
                                                        {lastName}?
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                    >
                                                        This cannot be undone
                                                    </Typography>
                                                </Box>
                                            );
                                            setConformationDialogTitle(
                                                "Delete user"
                                            );
                                            setConformationDialogSubmitButton(
                                                "Delete"
                                            );
                                            setConfirmationDialogOpen(true);
                                        }}
                                        disabled={
                                            disableAllFields ||
                                            isLoggedInUserManager
                                        }
                                        sx={{
                                            "&:hover": {
                                                backgroundColor:
                                                    "transparent !important"
                                            }
                                        }}
                                        disableRipple
                                    >
                                        <Typography
                                            variant="button"
                                            sx={{
                                                textTransform: "none",
                                                lineHeight: "16px"
                                            }}
                                        >
                                            {`Delete user`}
                                        </Typography>
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        display: "flex",
                        justifyContent: selectedUser ? "space-between" : "end"
                    }}
                >
                    {isDesktop && selectedUser && (
                        <Grid container sx={{ gap: "18px" }}>
                            <Button
                                color="primary"
                                variant="contained"
                                disabled={disableAllFields}
                                onClick={handleOpenUploadPhotoDialog}
                            >
                                Upload Photo
                            </Button>
                            {s3Link && (
                                <Avatar
                                    variant="rounded"
                                    src={s3Link}
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        borderRadius: "50%"
                                    }}
                                    alt="User"
                                />
                            )}
                        </Grid>
                    )}
                    <Grid
                        display={"flex"}
                        gap={"18px"}
                        width={!isDesktop ? "100%" : "auto"}
                    >
                        {isDesktop && (
                            <Button
                                onClick={onClose}
                                variant="cancelButton"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                        )}
                        <LoadingButton
                            color="primary"
                            variant="contained"
                            loading={isSubmitting}
                            disabled={!isDirty}
                            type="submit"
                            sx={{ width: !isDesktop && "100%" }}
                        >
                            Save
                        </LoadingButton>
                    </Grid>
                </DialogActions>
            </form>
        </>
    );
};

export default ProfileTab;
